import type { StoreApi } from 'zustand';
import { CommandRegistry } from '../core/commands/registry';
import { getEngine, type RenderEngine } from '../engine';
import { type EditorState, type ToastKind, useEditorStore } from '../state/editorStore';

/**
 * Everything a command or flow may touch, passed explicitly instead of imported
 * as a global. This is what makes the action layer unit-testable: pass a fake.
 */
export interface AppContext {
  store: StoreApi<EditorState>;
  getEngine(): RenderEngine | null;
  notify(message: string, kind?: ToastKind): void;
  askConfirm(request: {
    title: string;
    message: string;
    confirmLabel: string;
    cancelLabel?: string;
  }): Promise<boolean>;
}

export const appContext: AppContext = {
  store: useEditorStore,
  getEngine,
  notify: (message, kind) => useEditorStore.getState().notify(message, kind),
  askConfirm: (request) =>
    useEditorStore.getState().askConfirm({
      cancelLabel: 'Cancel',
      ...request,
    }),
};

export const appCommands = new CommandRegistry<AppContext>();

/**
 * The refusal the active-only commands give over a multi-selection
 * (Iteration 12, design doc §4): the menu row stays enabled, and running it
 * says out loud why nothing happened. A silent single-layer result under a
 * three-layer highlight would be the "nothing pretends" rule broken.
 *
 * Returns true when the refusal fired, so callers can bail out.
 */
export function refuseMultiSelection(ctx: AppContext, message: string): boolean {
  if (ctx.store.getState().selectedIds.size <= 1) return false;
  ctx.notify(message, 'warn');
  return true;
}
